import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { DrawerLink } from '../../app/DrawerLink';

import { EmojiButton } from '../../components/ui/EmojiPicker';
import { useEmojiComposer } from '../../lib/emoji';
import { useSession } from '../../stores/session';
import { rooms } from './api';

/**
 * Room chat.
 *
 * ## Read by anybody, written by an account
 *
 * Same shape as the rest of the site: reading is the advertisement, writing is
 * the reason to have an account. A signed-out visitor sees the conversation and
 * a prompt to join, not an input that fails when they use it.
 *
 * ## It follows the socket, and refetches on its own anyway
 *
 * `useRoom` invalidates this query when a `station.chat` event lands, which is
 * the fast path. The slow poll underneath is what covers a dropped connection -
 * §8.2's rule that the socket is a delivery mechanism, applied to the one part
 * of a room where nobody would notice they had stopped receiving anything.
 */
export function ChatPanel({
  username,
  divided = true,
  canModerate = false,
}: {
  username: string;
  /**
   * Whether to offer removal on each message.
   *
   * Passed in rather than worked out here, because the answer differs by
   * screen: the console knows it is the host's own room, a profile does not
   * without asking. The server checks regardless - this only decides whether
   * the control is drawn.
   */
  canModerate?: boolean;
  /**
   * Whether to rule off from whatever is above.
   *
   * True in the console, where this sits under the session figures. False on a
   * profile, where it is the whole column and the border would read as a stray
   * line across the top of it.
   */
  divided?: boolean;
}) {
  const user = useSession((state) => state.user);
  const queryClient = useQueryClient();
  const [body, setBody] = useState('');
  const emoji = useEmojiComposer(body, setBody);
  const scroller = useRef<HTMLDivElement>(null);

  const { data } = useQuery({
    queryKey: ['room', 'chat', username],
    queryFn: () => rooms.chat(username),
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
  });

  const remove = useMutation({
    mutationFn: (id: number) => rooms.removeMessage(username, id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['room', 'chat', username] }),
  });

  const say = useMutation({
    mutationFn: () => rooms.say(username, body.trim()),
    onSuccess: () => {
      setBody('');
      void queryClient.invalidateQueries({ queryKey: ['room', 'chat', username] });
    },
  });

  const items = data?.items ?? [];

  /*
   * Pinned to the bottom as messages arrive.
   *
   * Only when already near it: yanking somebody who scrolled up to read
   * something back to the newest line is the single most irritating thing a
   * chat panel can do.
   */
  useEffect(() => {
    const element = scroller.current;

    if (!element) return;

    const nearBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 120;

    if (nearBottom) element.scrollTop = element.scrollHeight;
  }, [items.length]);

  const submit = (event: FormEvent) => {
    event.preventDefault();

    if (body.trim() !== '') say.mutate();
  };

  return (
    <section className={`flex min-h-0 flex-1 flex-col gap-3 ${divided ? 'border-t border-line pt-5' : ''}`}>
      {/*
        Padded individually rather than on the section, so the message list's
        scrollbar reaches the edge of the column. Inset it and the bar floats in
        the middle of a margin, which reads as broken.
      */}
      <div className="flex items-center justify-between px-6">
        <span className="meta-caps text-faint">Room chat</span>
        {/* Slow mode is the route's rate limit rather than a column. Said here
            so somebody who hits it knows it was a rule, not a failure. */}
        <span className="font-mono text-[10px] text-ghost">20 a minute</span>
      </div>

      <div
        ref={scroller}
        className="flex min-h-[8rem] flex-1 flex-col gap-3 overflow-y-auto px-6"
        data-chat
      >
        {items.length === 0 && <p className="text-sm text-soft">Nobody has said anything yet.</p>}

        {items.map((message) => (
          <div key={message.id} className="group flex flex-col gap-0.5">
            <span className="flex items-center gap-2 font-mono text-[10px] text-accent">
              {message.from ? (
                <DrawerLink to={`/${message.from.username}`} className="hover:underline">
                  @{message.from.username}
                </DrawerLink>
              ) : (
                'someone'
              )}

              {/*
                Shown on hover rather than always. A row of delete buttons down
                a live conversation reads as a moderation queue; a host wants to
                read the room and reach for one when they need it.
              */}
              {canModerate && message.body !== null && (
                <button
                  type="button"
                  data-remove-message={message.id}
                  aria-label={`Remove message from ${message.from?.username ?? 'someone'}`}
                  title="Remove"
                  disabled={remove.isPending}
                  onClick={() => remove.mutate(message.id)}
                  className="text-ghost opacity-0 transition-opacity hover:text-hot-text focus:opacity-100 group-hover:opacity-100"
                >
                  remove
                </button>
              )}
            </span>
            {message.body === null ? (
              <span className="text-[13px] italic text-faint">Message removed.</span>
            ) : (
              <span className="text-[13px] leading-relaxed text-text-2">{message.body}</span>
            )}
          </div>
        ))}
      </div>

      {user ? (
        <form
          onSubmit={submit}
          className="mx-6 flex items-center gap-2 rounded-[var(--radius-pill)] border border-line bg-raised px-3 py-1.5"
        >
          <input
            ref={emoji.ref as React.RefObject<HTMLInputElement>}
            value={body}
            maxLength={500}
            data-chat-input
            placeholder="say something to the room"
            onChange={(event) => setBody(event.target.value)}
            className="min-w-0 flex-1 bg-transparent text-[13px] text-ink outline-none placeholder:text-faint"
          />
          <EmojiButton onSelect={emoji.insert} align="right" />
          <button
            type="submit"
            disabled={body.trim() === '' || say.isPending}
            className="shrink-0 font-mono text-[10px] text-accent disabled:text-ghost"
          >
            send
          </button>
        </form>
      ) : (
        <p className="px-6 text-xs text-faint">
          <Link to="/login" className="text-accent hover:underline">
            Sign in
          </Link>{' '}
          to say something.
        </p>
      )}
    </section>
  );
}
